import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { EQUIPMENT, MUSCLE_GROUPS, matchesMuscleFilter } from '@kraftwerk/shared';
import type { Equipment, MuscleFilter, MuscleGroup } from '@kraftwerk/shared';
import { db } from '../../db/local.js';
import type { LocalExercise } from '../../db/local.js';
import { createExercise, groupIntoFamilies, listExercises } from '../../db/exercises.js';
import { exerciseHighlights } from '../../db/workouts.js';
import { ExerciseEditor } from './ExerciseEditor.js';
import { ExerciseDetail } from './ExerciseDetail.js';
import { formatNumber } from './SessionScreen.js';

type Highlights = Awaited<ReturnType<typeof exerciseHighlights>>;

/**
 * Die Übungssammlung — eine schmale Liste, Tippen öffnet die Übungsseite.
 *
 * Früher trug jede Zeile zwei Knöpfe; bei über hundert Übungen war die Liste
 * mehr Knopf als Übung. Varianten anlegen und Bearbeiten wohnen jetzt auf der
 * Seite der Übung, wie bei den Gerichten.
 *
 * Übungen sind Nutzerdaten: Der Startbestand wurde beim Anlegen des Kontos
 * kopiert, alles daran ist änderbar.
 */
export function ExerciseLibrary(): React.JSX.Element {
  const { t } = useTranslation();
  const searchId = useId();
  const filterId = useId();
  const [query, setQuery] = useState('');
  const [equipment, setEquipment] = useState<Equipment | ''>('');
  const [muscles, setMuscles] = useState<MuscleFilter>(null);
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const exercises = useLiveQuery(() => listExercises(db), [], [] as LocalExercise[]);
  const highlights = useLiveQuery(() => exerciseHighlights(db), [], new Map() as Highlights);

  const families = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matches = exercises.filter((exercise) => {
      const label = `${exercise.name} ${exercise.variantLabel ?? ''}`.toLowerCase();
      const byName = needle === '' || label.includes(needle);
      const byEquipment = equipment === '' || exercise.equipment === equipment;
      return byName && byEquipment && matchesMuscleFilter(exercise.primaryMuscles, muscles);
    });
    return groupIntoFamilies(matches);
  }, [exercises, query, equipment, muscles]);

  if (openId) {
    return (
      // `min-h-dvh`: Die Seite lädt ihre Daten erst — ohne Mindesthöhe stünde
      // die Ansicht kurz leer und der Browser hielte die Scrollposition der Liste.
      <div className="flex min-h-dvh flex-col">
        <ExerciseDetail
          key={openId}
          exerciseId={openId}
          onBack={() => setOpenId(null)}
          onOpen={setOpenId}
        />
      </div>
    );
  }

  return (
    <section aria-labelledby="library-heading" className="flex flex-col gap-4">
      <header className="flex items-center justify-between gap-3">
        <h2 id="library-heading" className="text-lg font-semibold">
          {t('training.library.heading')}
        </h2>
        <button
          type="button"
          onClick={() => setCreating((value) => !value)}
          aria-expanded={creating}
          className="kw-btn kw-btn-small"
        >
          {t('training.library.new')}
        </button>
      </header>

      {creating ? (
        <ExerciseEditor
          onCancel={() => setCreating(false)}
          onSave={async (draft) => {
            const created = await createExercise(draft, db);
            setCreating(false);
            setOpenId(created.id);
          }}
        />
      ) : null}

      <div className="flex flex-wrap gap-2">
        <div className="flex min-w-40 flex-1 flex-col gap-1">
          <label htmlFor={searchId} className="text-xs text-ink-muted">
            {t('training.library.search')}
          </label>
          <input
            id={searchId}
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className="kw-field"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor={filterId} className="text-xs text-ink-muted">
            {t('training.library.equipment')}
          </label>
          <select
            id={filterId}
            value={equipment}
            onChange={(event) => setEquipment(event.target.value as Equipment | '')}
            className="kw-field"
          >
            <option value="">{t('training.library.anyEquipment')}</option>
            {EQUIPMENT.map((option) => (
              <option key={option} value={option}>
                {t(`training.equipment.${option}`)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <MuscleChips value={muscles} onChange={setMuscles} />

      {families.length === 0 ? (
        <p className="text-sm text-ink-muted">{t('training.library.empty')}</p>
      ) : (
        <ul className="kw-tile flex flex-col divide-y divide-line p-0">
          {families.map((family) => (
            <li key={family.root.id}>
              <ExerciseRow
                exercise={family.root}
                highlight={highlights.get(family.root.id)}
                onOpen={() => setOpenId(family.root.id)}
              />
              {family.variants.length > 0 ? (
                <ul className="flex flex-col pb-1 pl-5">
                  {family.variants.map((variant) => (
                    <li key={variant.id} className="border-l border-line">
                      <ExerciseRow
                        exercise={variant}
                        variant
                        highlight={highlights.get(variant.id)}
                        onOpen={() => setOpenId(variant.id)}
                      />
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Eine Zeile: Name, Muskeln, Bestwert — die ganze Zeile ist der Knopf. */
function ExerciseRow({
  exercise,
  variant,
  highlight,
  onOpen,
}: {
  exercise: LocalExercise;
  variant?: boolean;
  highlight: { bestOneRepMaxKg: number | null; sessions: number } | undefined;
  onOpen: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const name = variant && exercise.variantLabel ? exercise.variantLabel : exercise.name;
  const muscles = exercise.primaryMuscles.map((muscle) => t(`training.muscle.${muscle}`));

  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex min-h-11 w-full items-center gap-3 px-4 py-2 text-left hover:bg-surface-raised focus-visible:bg-surface-raised"
    >
      <span className="min-w-0 flex-1">
        <span className={`block truncate ${variant ? 'text-sm' : 'font-medium'}`}>
          {exercise.isFavorite ? (
            <span aria-label={t('training.exercise.isFavorite')} className="text-accent">
              ★{' '}
            </span>
          ) : null}
          {name}
        </span>
        {!variant && muscles.length > 0 ? (
          <span className="block truncate text-xs text-ink-muted">{muscles.join(', ')}</span>
        ) : null}
      </span>
      <span className="shrink-0 text-right text-xs text-ink-muted">
        {highlight && highlight.bestOneRepMaxKg !== null
          ? t('training.library.best', { kg: formatNumber(Math.round(highlight.bestOneRepMaxKg)) })
          : highlight
            ? t('training.library.sessions', { count: highlight.sessions })
            : t('training.library.untrained')}
      </span>
      <span aria-hidden="true" className="shrink-0 text-ink-muted">
        ›
      </span>
    </button>
  );
}

/**
 * Muskelfilter (25.10.2026, Timo): erst die Gruppe („Beine"), dann auf Wunsch
 * ein einzelner Muskel daraus. Eine Zeile, die waagerecht scrollt — Wischen
 * wechselt dort keinen Unterreiter.
 */
function MuscleChips({
  value,
  onChange,
}: {
  value: MuscleFilter;
  onChange: (filter: MuscleFilter) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const group: MuscleGroup | null =
    value === null
      ? null
      : 'group' in value
        ? value.group
        : ((Object.keys(MUSCLE_GROUPS) as MuscleGroup[]).find((key) =>
            (MUSCLE_GROUPS[key] as readonly string[]).includes(value.muscle),
          ) ?? null);
  const members = group ? MUSCLE_GROUPS[group] : [];

  return (
    <div role="group" aria-label={t('training.library.muscles')} className="flex flex-col gap-2">
      <div className="kw-chip-row">
        <button
          type="button"
          aria-pressed={value === null}
          onClick={() => onChange(null)}
          className="kw-toggle shrink-0"
        >
          {t('training.library.allMuscles')}
        </button>
        {(Object.keys(MUSCLE_GROUPS) as MuscleGroup[]).map((key) => (
          <button
            key={key}
            type="button"
            aria-pressed={group === key}
            onClick={() => onChange(group === key ? null : { group: key })}
            className="kw-toggle shrink-0"
          >
            {t(`training.muscleGroup.${key}`)}
          </button>
        ))}
      </div>
      {group && members.length > 1 ? (
        <div className="kw-chip-row">
          {members.map((muscle) => {
            const pressed = value !== null && 'muscle' in value && value.muscle === muscle;
            return (
              <button
                key={muscle}
                type="button"
                aria-pressed={pressed}
                onClick={() => onChange(pressed ? { group } : { muscle })}
                className="kw-toggle shrink-0"
              >
                {t(`training.muscle.${muscle}`)}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
